import React, { useCallback, useEffect, useState } from 'react';
import { Plus, Trash2, Save, Eye, EyeOff, RefreshCw, ArrowUp, ArrowDown } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const TickerTab = ({ token }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [newText, setNewText] = useState('');
  const [newLink, setNewLink] = useState('');
  const [busyId, setBusyId] = useState(null);

  const auth = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API}/api/admin/ticker`, { headers: auth });
      if (!res.ok) throw new Error('Failed to load headlines');
      setItems(await res.json());
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const create = async (e) => {
    e.preventDefault();
    if (!newText.trim()) return;
    setBusyId('new');
    try {
      const res = await fetch(`${API}/api/admin/ticker`, {
        method: 'POST',
        headers: { ...auth, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          text: newText.trim(),
          link: newLink.trim() || null,
          active: true,
          order: items.length,
        }),
      });
      if (!res.ok) throw new Error('Create failed');
      setNewText('');
      setNewLink('');
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusyId(null);
    }
  };

  const patch = async (id, updates) => {
    setBusyId(id);
    try {
      const res = await fetch(`${API}/api/admin/ticker/${id}`, {
        method: 'PATCH',
        headers: { ...auth, 'Content-Type': 'application/json' },
        body: JSON.stringify(updates),
      });
      if (!res.ok) throw new Error('Update failed');
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this headline?')) return;
    setBusyId(id);
    try {
      const res = await fetch(`${API}/api/admin/ticker/${id}`, { method: 'DELETE', headers: auth });
      if (!res.ok) throw new Error('Delete failed');
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusyId(null);
    }
  };

  const move = async (id, dir) => {
    const idx = items.findIndex((x) => x.id === id);
    const swap = idx + dir;
    if (swap < 0 || swap >= items.length) return;
    const a = items[idx];
    const b = items[swap];
    await patch(a.id, { order: b.order });
    await patch(b.id, { order: a.order });
  };

  const updateField = (id, field, value) => {
    setItems((prev) => prev.map((x) => (x.id === id ? { ...x, [field]: value } : x)));
  };

  return (
    <div className="space-y-5" data-testid="ticker-tab">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <p className="text-sm text-gray-400">
          These headlines power the red <span className="text-red-400 font-bold">BREAKING</span> ticker on every page.
          When this list is empty, the ticker falls back to your latest published stories.
        </p>
        <div className="flex items-center gap-2">
          <button
            onClick={load}
            data-testid="ticker-refresh-btn"
            className="flex items-center gap-1.5 bg-zinc-900 hover:bg-zinc-800 border border-red-900/30 text-gray-300 hover:text-white font-bold px-3 py-1.5 rounded text-xs"
          >
            <RefreshCw className="h-3.5 w-3.5" />
            Refresh
          </button>
        </div>
      </div>

      {/* Create form */}
      <form
        onSubmit={create}
        className="bg-zinc-900/80 backdrop-blur-xl border border-red-900/30 rounded-lg p-4 space-y-3"
        data-testid="ticker-create-form"
      >
        <p className="text-xs font-black tracking-widest text-red-400 uppercase">Add Headline</p>
        <input
          type="text"
          required
          value={newText}
          onChange={(e) => setNewText(e.target.value)}
          placeholder="BREAKING: e.g. Northern Suburbs Standoff - Live Updates"
          data-testid="ticker-new-text"
          maxLength={300}
          className="w-full bg-black/50 border border-red-900/40 text-white px-3 py-2 rounded text-sm focus:outline-none focus:border-red-500"
        />
        <div className="flex flex-col sm:flex-row gap-2">
          <input
            type="text"
            value={newLink}
            onChange={(e) => setNewLink(e.target.value)}
            placeholder="Optional link (https://... or /explicit)"
            data-testid="ticker-new-link"
            className="flex-1 bg-black/50 border border-red-900/40 text-white px-3 py-2 rounded text-sm focus:outline-none focus:border-red-500"
          />
          <button
            type="submit"
            disabled={busyId === 'new'}
            data-testid="ticker-add-btn"
            className="bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white font-black px-4 py-2 rounded text-sm flex items-center justify-center gap-2"
          >
            <Plus className="h-4 w-4" />
            ADD
          </button>
        </div>
      </form>

      {error && (
        <div className="bg-red-900/20 border border-red-500/50 text-red-400 px-4 py-3 rounded text-sm" data-testid="ticker-error">
          {error}
        </div>
      )}

      {loading ? (
        <div className="text-gray-400" data-testid="ticker-loading">Loading…</div>
      ) : items.length === 0 ? (
        <div className="bg-zinc-900/60 border border-red-900/30 rounded-lg p-8 text-center text-gray-400" data-testid="ticker-empty">
          No headlines yet. The public ticker is showing your latest stories until you add one.
        </div>
      ) : (
        <div className="space-y-3" data-testid="ticker-list">
          {items.map((h, idx) => (
            <div
              key={h.id}
              data-testid={`ticker-item-${h.id}`}
              className={`bg-zinc-900/80 backdrop-blur-xl border rounded-lg p-3 space-y-2 ${
                h.active ? 'border-red-900/30' : 'border-zinc-700/50 opacity-60'
              }`}
            >
              <div className="flex items-start gap-2">
                <span className="text-red-500 font-black tabular-nums w-6 text-right pt-2">{idx + 1}</span>
                <div className="flex-1 space-y-2">
                  <input
                    type="text"
                    value={h.text}
                    onChange={(e) => updateField(h.id, 'text', e.target.value)}
                    data-testid={`ticker-text-${h.id}`}
                    maxLength={300}
                    className="w-full bg-black/50 border border-red-900/40 text-white px-3 py-2 rounded text-sm focus:outline-none focus:border-red-500"
                  />
                  <input
                    type="text"
                    value={h.link || ''}
                    onChange={(e) => updateField(h.id, 'link', e.target.value)}
                    placeholder="Optional link"
                    data-testid={`ticker-link-${h.id}`}
                    className="w-full bg-black/50 border border-red-900/40 text-white px-3 py-2 rounded text-xs focus:outline-none focus:border-red-500"
                  />
                </div>
              </div>
              <div className="flex items-center justify-end gap-2 flex-wrap">
                <button
                  onClick={() => move(h.id, -1)}
                  disabled={idx === 0 || busyId === h.id}
                  data-testid={`ticker-up-${h.id}`}
                  className="bg-zinc-800 hover:bg-zinc-700 disabled:opacity-30 text-gray-300 p-1.5 rounded"
                  aria-label="Move up"
                >
                  <ArrowUp className="h-3.5 w-3.5" />
                </button>
                <button
                  onClick={() => move(h.id, 1)}
                  disabled={idx === items.length - 1 || busyId === h.id}
                  data-testid={`ticker-down-${h.id}`}
                  className="bg-zinc-800 hover:bg-zinc-700 disabled:opacity-30 text-gray-300 p-1.5 rounded"
                  aria-label="Move down"
                >
                  <ArrowDown className="h-3.5 w-3.5" />
                </button>
                <button
                  onClick={() => patch(h.id, { active: !h.active })}
                  disabled={busyId === h.id}
                  data-testid={`ticker-toggle-${h.id}`}
                  className={`flex items-center gap-1 px-2.5 py-1.5 rounded text-xs font-bold ${
                    h.active
                      ? 'bg-emerald-700/40 hover:bg-emerald-600/60 text-emerald-200'
                      : 'bg-zinc-800 hover:bg-zinc-700 text-gray-400'
                  }`}
                >
                  {h.active ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />}
                  {h.active ? 'ACTIVE' : 'HIDDEN'}
                </button>
                <button
                  onClick={() => patch(h.id, { text: h.text, link: h.link || null })}
                  disabled={busyId === h.id}
                  data-testid={`ticker-save-${h.id}`}
                  className="flex items-center gap-1 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white font-bold text-xs px-3 py-1.5 rounded"
                >
                  <Save className="h-3.5 w-3.5" />
                  Save
                </button>
                <button
                  onClick={() => remove(h.id)}
                  disabled={busyId === h.id}
                  data-testid={`ticker-delete-${h.id}`}
                  className="flex items-center gap-1 bg-red-700/40 hover:bg-red-600/60 text-red-200 font-bold text-xs px-3 py-1.5 rounded"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default TickerTab;
